import { useState } from 'react';
import Dropdown, { withValue } from './Dropdown';
import Field from './Field';
import Icon from './Icon';
import { ErrorBanner } from './Feedback';
import useForm from '../hooks/useForm';
import * as titleRules from '../validation/titleRules';
import { api } from '../api/client';

/**
 * The form a title is added and edited with, and the tile it is listed under.
 *
 * <p>Lifted out of a TitleCatalogueModal that no longer exists. That modal was a table
 * of titles with the form under it, opened from three other screens; the Content
 * Library now opens on the books themselves and carries Edit and Retire on each card,
 * so the table had nothing left to show and its last caller went with the Titles button
 * on Activation Codes. The form is the part worth keeping — a code fixed after
 * creation, a subject read off the master, and a cover that uploads at a different
 * moment on create than on edit is not a thing to have two copies of.
 *
 * <p>Subject and Class are picked, not typed. They were free text, which is how the
 * catalogue came to hold "Social Studies" beside a Subject Master that says "Social
 * Science": two names for one subject, and a repository tree that groups the same
 * books under both. Subject reads the master; Class reads the fixed list of classes a
 * book can be written for, which is not the same question as the bands a teacher says
 * they handle.
 */

const EMPTY = { name: '', shortName: '', code: '', subject: '', classLevel: '' };

/** The cover tile, shown wherever a title is listed. */
export function CoverThumb({ url }) {
  return (
    <span className="cover-thumb">
      {url ? <img src={url} alt="" /> : <span className="tiny muted">None</span>}
    </span>
  );
}

/**
 * One form for both adding and editing.
 *
 * <p>Deliberately the same component: a field added to the add form that is missing
 * from the edit form is a field nobody can correct, and the catalogue is small enough
 * that every title gets edited eventually.
 *
 * <p>The cover is handled apart from the rest, because it is a file and the rest is
 * JSON. On an edit it goes up as soon as it is chosen, against a title that already
 * exists. On a create there is nothing to attach it to yet, so the title is created
 * first and the image follows — and if that second call fails, the title still stands
 * and the message says the cover is what did not take, rather than leaving the person
 * to guess whether anything was saved.
 */
export default function TitleForm({ title, subjects, bookClasses, onCancel, onSaved, onError }) {
  const editingId = title?.id ?? null;
  const [coverUrl, setCoverUrl] = useState(title?.coverUrl ?? null);
  // Held until save on a create; sent immediately on an edit.
  const [pendingCover, setPendingCover] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputId = `title-cover-${editingId ?? 'new'}`;

  const form = useForm({
    initialValues: title
      ? {
          name: title.name ?? '',
          shortName: title.shortName ?? '',
          code: title.code ?? '',
          subject: title.subject ?? '',
          classLevel: title.classLevel ?? '',
        }
      : EMPTY,
    validators: {
      name: titleRules.name,
      shortName: titleRules.shortName,
      // The code is fixed once created, so an edit has nothing to validate.
      ...(editingId ? {} : { code: titleRules.code }),
      subject: titleRules.subject,
      classLevel: titleRules.classLevel,
    },
  });

  async function chooseCover(file) {
    if (!file) return;
    setError(null);
    if (!editingId) {
      // Nothing to attach it to yet. Previewed locally and sent after the create.
      setPendingCover(file);
      setCoverUrl(URL.createObjectURL(file));
      return;
    }
    setCoverBusy(true);
    try {
      const updated = await api.uploadTitleCover(editingId, file);
      setCoverUrl(updated.coverUrl);
    } catch (err) {
      setError(err);
    } finally {
      setCoverBusy(false);
    }
  }

  async function dropCover() {
    setError(null);
    if (!editingId) {
      setPendingCover(null);
      setCoverUrl(null);
      return;
    }
    setCoverBusy(true);
    try {
      const updated = await api.removeTitleCover(editingId);
      setCoverUrl(updated.coverUrl ?? null);
    } catch (err) {
      setError(err);
    } finally {
      setCoverBusy(false);
    }
  }

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    const body = {
      name: values.name.trim(),
      shortName: values.shortName.trim(),
      subject: values.subject || null,
      classLevel: values.classLevel || null,
    };
    try {
      if (editingId) {
        await api.updateTitle(editingId, body);
        await onSaved();
        return;
      }
      const created = await api.createTitle({ ...body, code: values.code.trim() });
      if (pendingCover) {
        try {
          await api.uploadTitleCover(created.id, pendingCover);
        } catch (err) {
          // The title exists; say which half failed rather than implying neither did.
          onError(err);
        }
      }
      form.reset(EMPTY);
      setPendingCover(null);
      setCoverUrl(null);
      await onSaved();
    } catch (err) {
      if (!form.applyServerErrors(err, titleRules.SERVER_CODES)) setError(err);
    }
  });

  return (
    <form className="stack" {...form.formProps} onSubmit={submit}>
      <div className="row-between">
        <b>{editingId ? `Edit ${title.name}` : 'Add a title'}</b>
        {editingId && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      <Field label="Title name" id="name" required error={form.errorFor('name')}>
        <input className="input" maxLength={titleRules.LIMITS.name} {...form.field('name')} />
      </Field>

      <div className="grid-2">
        <Field
          label="Short name"
          id="shortName"
          required
          error={form.errorFor('shortName')}
          hint="Used as the column header in the adoption grid"
        >
          <input className="input" maxLength={titleRules.LIMITS.shortName} {...form.field('shortName')} />
        </Field>
        <Field
          label="Title code"
          id="code"
          required={!editingId}
          error={form.errorFor('code')}
          hint={editingId ? 'Fixed once created' : 'Fixed once created, e.g. FH-MATH-8'}
        >
          <input
            className="input"
            maxLength={titleRules.LIMITS.code}
            disabled={Boolean(editingId)}
            {...form.field('code')}
          />
        </Field>
      </div>

      <div className="grid-2">
        <Field label="Subject" id="subject" error={form.errorFor('subject')}>
          {/* withValue, so a title filed under a subject the master no longer lists
              opens showing that subject rather than an empty box that saves as blank. */}
          <Dropdown
            id="subject"
            options={withValue(subjects, form.values.subject)}
            value={form.values.subject}
            onChange={(subject) => form.setValue('subject', subject)}
            placeholder="Choose a subject"
            emptyLabel="No matching subject"
            error={form.errorFor('subject')}
          />
        </Field>
        <Field label="Class" id="classLevel" error={form.errorFor('classLevel')}>
          <Dropdown
            id="classLevel"
            options={withValue(bookClasses, form.values.classLevel)}
            value={form.values.classLevel}
            onChange={(classLevel) => form.setValue('classLevel', classLevel)}
            placeholder="Choose a class"
            emptyLabel="No matching class"
            error={form.errorFor('classLevel')}
          />
        </Field>
      </div>

      <Field
        label="Cover image"
        id={inputId}
        hint="PNG, JPEG or WebP, up to 2 MB. Optional — a book without one shows its initials on the student’s shelf."
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s1)' }}>
          <CoverThumb url={coverUrl} />
          <span style={{ display: 'flex', gap: 'var(--s0)' }}>
            <label htmlFor={inputId} className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
              {coverBusy ? 'Uploading…' : coverUrl ? 'Replace' : 'Choose image'}
            </label>
            <input
              id={inputId}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              disabled={coverBusy}
              onChange={(event) => chooseCover(event.target.files?.[0])}
            />
            {coverUrl && (
              <button type="button" className="btn btn-ghost btn-sm" disabled={coverBusy} onClick={dropCover}>
                Remove
              </button>
            )}
          </span>
        </div>
      </Field>

      <div>
        <button type="submit" className="btn btn-primary" disabled={form.submitting}>
          <Icon name="check" size={15} />
          {form.submitting ? 'Saving…' : editingId ? 'Save changes' : 'Add title'}
        </button>
      </div>
    </form>
  );
}
